/**
 * The scene's GPU point systems (lifted from lantern-water's
 * `ambient-motion.js`): drifting dust, chimney smoke and falling leaves, each
 * one `THREE.Points` whose buffers never change after they are built; every
 * path is a function of a seed and the time in the vertex shader, and only a
 * few uniforms change per frame. The record says how many of each and where
 * they start (`emitters`: the dust's box, the chimney tops, the leaves'
 * area); the paths and the shading stay in the GLSL they were tuned in. The
 * seeds come from one stream in the prototype's order (dust, smoke, leaves),
 * so the counts of 230, 60 and 28 build the prototype's positions.
 *
 * Medium draws them (the stress test); Off draws none (the Rendering page:
 * no particles at Off). Indoors (an interior scene) only the dust shows, in
 * the room's smaller box (`room`), as the prototype's shop.
 */
import type { Scene } from '@ab/content-schema';
import * as THREE from 'three';
import { seededRandom } from '../grid.ts';
import { sceneLook } from './constants.ts';

type Emitter = Scene['emitters'][number];
/** The town's kinds (the airship's motes are `ship/motes.ts`). */
export type ParticleKind = Exclude<Emitter['kind'], 'motes'>;

/** The prototype's one seed for every system's draws. */
const seed = 448;

const pathOf = (kind: ParticleKind): string => {
  switch (kind) {
    case 'dust':
      return `p.x=mod(p.x+20.+t*.13,40.)-20.;p.z=mod(p.z+16.+t*.08,32.)-16.;
        p.y=mod(p.y+t*(.025+seed.w*.025),7.);
        p.x+=sin(t*.45+seed.x*6.28)*.24;
        p*=mix(vec3(1.),vec3(.28,.6,.28),room);
        fade=smoothstep(0.,.5,p.y)*(1.-smoothstep(mix(6.,3.6,room),mix(7.,4.2,room),p.y));`;
    case 'smoke':
      return `float life=fract(seed.x+t*(.035+seed.y*.009));
        p+=vec3(life*1.7+sin(t*.38+seed.z*6.28)*life*.25,life*3.2,life*.5);
        fade=sin(life*3.14159)*.18;`;
    case 'leaves':
      return `float life=fract(seed.x+t*(.035+seed.y*.02));
        p+=vec3(life*2.6+sin(life*9.+seed.z*6.28)*.3,3.8*(1.-life),life*1.2);
        fade=smoothstep(0.,.12,life)*(1.-smoothstep(.8,1.,life))*.75;`;
  }
};

const colorOf = (kind: ParticleKind): string => {
  switch (kind) {
    case 'dust':
      return 'vec3(.9,.79,.52)';
    case 'smoke':
      return 'vec3(.65,.66,.60)';
    case 'leaves':
      return 'mix(vec3(.48,.24,.08),vec3(.85,.61,.22),tint)';
  }
};

const material = (kind: ParticleKind, size: number, maxSize: number): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, room: { value: 0 }, night: { value: 0 }, pixelScale: { value: 600 } },
    transparent: true,
    depthWrite: false,
    vertexShader: `attribute vec4 seed;uniform float time,room,pixelScale;varying float fade;varying float turn;varying float tint;
void main(){
  vec3 p=position;float t=time;tint=seed.z;
  ${pathOf(kind)}
  turn=t*(.6+seed.w)+seed.z*6.28;
  vec4 mv=modelViewMatrix*vec4(p,1.);gl_Position=projectionMatrix*mv;
  gl_PointSize=clamp(pixelScale*${size.toFixed(3)}/max(1.,-mv.z),1.,${maxSize.toFixed(1)});
}`,
    fragmentShader: `uniform float night;varying float fade,turn,tint;
void main(){vec2 q=gl_PointCoord-.5;
  ${kind === 'leaves' ? 'q=mat2(cos(turn),-sin(turn),sin(turn),cos(turn))*q;q.x*=1.7;' : ''}
  float alpha=(1.-smoothstep(.16,.5,length(q)))*fade;
  vec3 color=${colorOf(kind)};
  alpha*=mix(1.,${kind === 'dust' ? '.4' : '.55'},night);
  gl_FragColor=vec4(color,alpha*${kind === 'dust' ? '.55' : '1.'});
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`,
  });

export type Particles = {
  readonly group: THREE.Group;
  /** Points per kind drawn now, read from the live buffers (0 for a kind the scene has not, while off, or under reduced motion). */
  counts(): Readonly<Record<ParticleKind, number>>;
  setActive(active: boolean): void;
  /** `enabled` false (reduced motion) hides them, as the prototype's `ambient.update(…, enabled)`. */
  update(time: number, night: number, pixelScale: number, enabled: boolean): void;
  dispose(): void;
};

/** The scene's emitters in three.js axes (z = −y); `indoor` keeps the dust in the room's box. */
export const createParticles = (emitters: Scene['emitters'], indoor: boolean): Particles => {
  const random = seededRandom(seed);
  const group = new THREE.Group();
  group.name = 'ambient-motion';
  const systems: { readonly kind: ParticleKind; readonly points: THREE.Points; readonly material: THREE.ShaderMaterial; readonly geometry: THREE.BufferGeometry; readonly count: number }[] = [];
  const order: readonly ParticleKind[] = ['dust', 'smoke', 'leaves'];
  for (const kind of order) {
    for (const e of emitters.filter((x) => x.kind === kind)) {
      const positions: number[] = [];
      const seeds: number[] = [];
      const sources = e.sources ?? [];
      const area = e.area;
      for (let i = 0; i < e.count; i += 1) {
        if (kind === 'smoke') {
          const s = sources[i % Math.max(1, sources.length)];
          positions.push(s?.x ?? 0, s?.h ?? 0, -(s?.y ?? 0));
        } else if (kind === 'leaves' && area !== undefined) {
          const cx = (area.x0 + area.x1) / 2;
          const cz = -(area.y0 + area.y1) / 2;
          positions.push(cx + (random() - 0.5) * (area.x1 - area.x0), 0, cz + (random() - 0.5) * (area.y1 - area.y0));
        } else if (area !== undefined) {
          positions.push((random() - 0.5) * (area.x1 - area.x0), random() * (e.height ?? 0), (random() - 0.5) * (area.y1 - area.y0));
        } else positions.push(0, 0, 0);
        seeds.push(random(), random(), random(), random());
      }
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      geometry.setAttribute('seed', new THREE.Float32BufferAttribute(seeds, 4));
      const m = material(kind, e.size ?? 0, e.maxSize ?? 0);
      m.uniforms['room'] = { value: indoor ? 1 : 0 };
      const points = new THREE.Points(geometry, m);
      points.frustumCulled = false;
      points.renderOrder = sceneLook.particles.renderOrder;
      points.name = kind;
      group.add(points);
      systems.push({ kind, points, material: m, geometry, count: e.count });
    }
  }
  let active = false;
  group.visible = false;
  return {
    group,
    counts() {
      const out: Record<ParticleKind, number> = { dust: 0, smoke: 0, leaves: 0 };
      if (!group.visible) return out;
      for (const s of systems) {
        if (!s.points.visible) continue;
        const n = s.geometry.getAttribute('position').count;
        out[s.kind] += Math.min(n, s.geometry.drawRange.count);
      }
      return out;
    },
    setActive(on) {
      active = on;
      group.visible = on;
      // Off frees the points' GPU buffers and programs (they upload and compile again when Medium draws them).
      if (!on) {
        for (const s of systems) {
          s.geometry.dispose();
          s.material.dispose();
        }
      }
    },
    update(time, night, pixelScale, enabled) {
      group.visible = active && enabled;
      for (const s of systems) {
        const u = s.material.uniforms;
        if (u['time'] !== undefined) u['time'].value = time;
        if (u['night'] !== undefined) u['night'].value = night;
        if (u['pixelScale'] !== undefined) u['pixelScale'].value = pixelScale;
      }
    },
    dispose() {
      group.removeFromParent();
      for (const s of systems) {
        s.geometry.dispose();
        s.material.dispose();
      }
    },
  };
};
